import cx from 'classnames'
import { useState } from 'react'
import { useDebouncedCallback } from '@react-hookz/web'

import Styles from './Link.module.scss'
import ImagePickerModalLinkStyles from 'components/ImagePickerModal/Link/Link.module.scss'
import ImageMenuModalStyles from '../ImageMenuModal.module.scss'

import { useImageMenuContext } from '../ImageMenuContext'

import { validateImageUrl } from 'components/ImagePickerModal/Link/validators'

import { Modal } from 'almanac-ui/Modal'
import { SearchField } from 'components/ImagePickerModal/SearchField/SearchField'
import { EmptyState } from 'components/ImagePickerModal/Link/EmptyState'
import { Error } from 'components/ArticlePostPreview/PostPreviewBody/Error'
import { MediaFileThumbnail } from 'components/ImagePickerModal/ImageThumbnail/MediaFileThumbnail'
import { Actions } from '../ImageMenuModal'
import { useIsSystemRedesign } from 'hooks/useIsSystemRedesign'

export const Link = () => {
  const isSystemRedesignEnabled = useIsSystemRedesign()
  const { setSelectedImages, selectedImages, toggleSelectedImage, setProvider } = useImageMenuContext()
  const [link, setLink] = useState('')
  const [validationState, setValidationState] = useState<
    'empty' | 'invalid' | 'valid-url' | 'valid-image' | 'valid-base64'
  >('empty')

  const debouncedValidation = useDebouncedCallback(
    (url: string) => {
      const result = validateImageUrl(url)
      setValidationState(result.state)

      if (result.state === 'valid-image' || result.state === 'valid-base64') {
        setSelectedImages([{ url: result.url, provider: 'link' }])
      } else {
        setSelectedImages([])
      }
    },
    [setSelectedImages],
    500,
  )

  const handleLinkChange = (newLink: string) => {
    setLink(newLink)
    debouncedValidation(newLink)
  }

  const renderContent = () => {
    switch (validationState) {
      case 'empty':
        return <EmptyState />
      case 'invalid':
        return <Error errorMessages={['Please enter a valid URL']} />
      case 'valid-url':
        return (
          <Error
            errorMessages={[
              'The URL must be a direct link to an image file (ending in .jpg, .png, etc) or a base64 encoded image. For example: https://example.com/image.jpg',
            ]}
          />
        )
      case 'valid-image':
      case 'valid-base64':
        return (
          <MediaFileThumbnail
            mediaFile={{ url: link }}
            onToggleSelect={(image) => toggleSelectedImage({ ...image, provider: 'link' })}
            isSelected={selectedImages.some((i) => i.url === link)}
            controls
          />
        )
      default:
        return null
    }
  }

  return (
    <>
      <Modal.Header
        className={ImageMenuModalStyles.header}
        actions={<Actions />}
        onBack={() => setProvider('')}
        title="Add from Link"
      />
      <Modal.Content className={Styles.content} withoutFooter={isSystemRedesignEnabled}>
        <div data-testid="add-from-link-content" className={cx(ImagePickerModalLinkStyles.link, Styles.content)}>
          <SearchField value={link} onChange={handleLinkChange} placeholder="https://" />

          <div className={cx(ImagePickerModalLinkStyles.preview, Styles.preview)}>{renderContent()}</div>
        </div>
      </Modal.Content>
    </>
  )
}
